import { useEffect, useState } from "react";

// From this width the sidebar (or its rail) is part of the page; under it the sidebar is a drawer opened from the header's menu button.
const WIDE_QUERY = "(min-width: 1320px)";

/** Whether the viewport is wide enough for the sidebar. True where matchMedia is missing (jsdom). */
export const useIsWide = () => {
  const [wide, setWide] = useState(() => (window.matchMedia ? window.matchMedia(WIDE_QUERY).matches : true));

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const query = window.matchMedia(WIDE_QUERY);
    const update = () => setWide(query.matches);

    update();
    query.addEventListener("change", update);

    return () => query.removeEventListener("change", update);
  }, []);

  return wide;
};

/**
 * Closes a popover on Escape (focus goes back to what opened it) and on a press outside `containerRef`.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {{current: ?HTMLElement}} containerRef the popover and its trigger
 * @param {{current: ?HTMLElement}} triggerRef
 */
export const useDismiss = (open, onClose, containerRef, triggerRef) => {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = event => {
      if (event.key === "Escape") {
        onClose();
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
      }
    };
    const onPointerDown = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, onClose, containerRef, triggerRef]);
};
